"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import {
  Filter,
  Eye,
  Trash2,
  Building2,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  X,
  Edit,
  Save,
  RefreshCw,
  Briefcase,
  Award,
  BarChart3,
  Heart,
  Users,
  Settings,
  CreditCard
} from "lucide-react"
import { getStaticOption, setStaticOption } from '../utils/api';

const FundingDashboard = () => {
  const [fundings, setFundings] = useState([])
  const [filteredFundings, setFilteredFundings] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showPopup, setShowPopup] = useState(false)
  const [selectedFunding, setSelectedFunding] = useState(null)
  const [editingStatusId, setEditingStatusId] = useState(null)
  const [isEditingAmount, setIsEditingAmount] = useState(false)
  const [newAmountRaised, setNewAmountRaised] = useState(0)
  const [showCancelPopup, setShowCancelPopup] = useState(null)
  const [newStatus, setNewStatus] = useState("")
  const [loading, setLoading] = useState(false)
  const [interests, setInterests] = useState([])
  const [showInterestsModal, setShowInterestsModal] = useState(false)
  const [currentFundingInterests, setCurrentFundingInterests] = useState([])
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [fundingRegistrationAmount, setFundingRegistrationAmount] = useState(0)
  const [isEditing, setIsEditing] = useState(false);
  const [amount, setAmount] = useState(0);


  const [filters, setFilters] = useState({
    sector: "",
    location: "",
    stage: "",
    startDate: "",
    endDate: "",
  })

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/funding-registration`)
        setFundingRegistrationAmount(response.data.amount || 0)
      } catch (error) {
        console.error("Error fetching funding registration amount:", error)
      }
    }
    fetchSettings()
  }, [])

  // Add this function to handle saving the new amount
  const handleSaveFundingAmount = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/funding-registration`, {
        amount: fundingRegistrationAmount
      })
      setIsEditingAmount(false)
    } catch (error) {
      console.error("Error updating funding registration amount:", error)
    }
  }

  const STATUS_COLORS = {
    waiting: "bg-blue-100 text-blue-800 border-blue-200",
    "on hold": "bg-yellow-100 text-yellow-800 border-yellow-200",
    approved: "bg-green-100 text-green-800 border-green-200",
    Pending: "bg-gray-100 text-gray-800 border-gray-200",
  }

  const PAYMENT_STATUS_COLORS = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    failed: "bg-red-100 text-red-800 border-red-200",
    // Add more statuses as needed
  }

  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/fundings/update-status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      })
      const data = await res.json()
      if (res.ok) {
        const updatedFundings = fundings.map((f) => (f._id === id ? { ...f, status } : f))
        setFundings(updatedFundings)
        applySearchFilter(searchTerm, updatedFundings)
        setEditingStatusId(null)
      } else {
        console.error(data.message)
      }
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  const fetchFundings = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/fundings`)
      const fundingsData = response.data

      const fundingsWithStartup = await Promise.all(
        fundingsData.map(async (funding) => {
          try {
            const startupRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${funding.userId}`)
            return {
              ...funding,
              startupDetails: startupRes.data,
            }
          } catch (err) {
            console.error(`Error fetching startup for userId ${funding.userId}:`, err)
            return funding
          }
        }),
      )

      setFundings(fundingsWithStartup)
      setFilteredFundings(fundingsWithStartup)
    } catch (error) {
      console.error("Error fetching fundings:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchInterests = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/interests`)
      setInterests(response.data)
    } catch (error) {
      console.error("Error fetching interests:", error)
    }
  }, [])

  useEffect(() => {
    fetchFundings()
    fetchInterests()
  }, [fetchFundings, fetchInterests])

  const getInterestsForFunding = useCallback((fundingId) => {
    return interests.filter(interest => interest.fundingId === fundingId)
  }, [interests])

  const handleViewInterests = useCallback((fundingId) => {
    const fundingInterests = getInterestsForFunding(fundingId)
    setCurrentFundingInterests(fundingInterests)
    setShowInterestsModal(true)
  }, [getInterestsForFunding])

  const handleCancel = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/fundings/${id}`, {
        method: "DELETE",
      })
      const data = await response.json()
      if (response.ok) {
        setFundings((prevFundings) => prevFundings.filter((funding) => funding._id !== id))
        setFilteredFundings((prevFundings) => prevFundings.filter((funding) => funding._id !== id))
      } else {
        alert(data.message || "Failed to delete funding")
      }
    } catch (error) {
      console.error("Error deleting funding:", error)
      alert("An error occurred while deleting the funding.")
    }
  }

  const handleView = useCallback((funding) => {
    setSelectedFunding(funding)
    setNewAmountRaised(funding.amountRaised || 0)
    setShowPopup(true)
  }, [])

  const handleSearch = useCallback((e) => {
    const value = e.target.value.toLowerCase()
    setSearchTerm(value)
    applySearchFilter(value, fundings)
  }, [fundings])

  const applySearchFilter = useCallback((search, data) => {
    const filtered = data.filter(
      (funding) =>
        funding.sector?.toLowerCase().includes(search) ||
        funding.location?.toLowerCase().includes(search) ||
        funding.stage?.toLowerCase().includes(search),
    )
    setFilteredFundings(filtered)
  }, [])

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({
      sector: "",
      location: "",
      stage: "",
      startDate: "",
      endDate: "",
    })
  }, [])

  const filteredRows = useMemo(() => {
    return filteredFundings.filter((funding) => {
      const createdAt = new Date(funding.createdAt)
      const start = filters.startDate ? new Date(filters.startDate) : null
      const end = filters.endDate ? new Date(filters.endDate) : null
      return (
        funding.sector.toLowerCase().includes(filters.sector.toLowerCase()) &&
        funding.location.toLowerCase().includes(filters.location.toLowerCase()) &&
        funding.stage.toLowerCase().includes(filters.stage.toLowerCase()) &&
        (!start || createdAt >= start) &&
        (!end || createdAt <= end)
      )
    })
  }, [filteredFundings, filters])

  const updateAmountRaised = useCallback(async (fundingId, amount) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/fundings/update-amount/${fundingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amountRaised: amount }),
      })
      const data = await res.json()
      if (res.ok) {
        const updatedFundings = fundings.map((funding) =>
          funding._id === fundingId ? { ...funding, amountRaised: amount } : funding,
        )
        setFundings(updatedFundings)
        setSelectedFunding((prev) => ({
          ...prev,
          amountRaised: amount,
        }))
        setIsEditingAmount(false)
      } else {
        console.error(data.message || "Failed to update amountRaised")
      }
    } catch (error) {
      console.error("Error updating amountRaised:", error)
    }
  }, [fundings])

  const getEmbedUrl = useCallback((url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  }, []);

  const ModalOverlay = useCallback(({ children, onClose }) => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </motion.div>
      </motion.div>
    );
  }, []);

   useEffect(() => {
    const fetchAmount = async () => {
      try {
        const value = await getStaticOption('funding-registration');
        setAmount(value ?? 0);
      } catch (err) {
        console.error('Error loading funding-registration option:', err);
        setError('Failed to load setting');
      }
    };
    fetchAmount();
  }, []);

  const handleSave = async () => {
    try {
      await setStaticOption('funding-registration', amount);
      setIsEditing(false);
    } catch (err) {
      // handle or show error message
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[95rem] mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Funding Dashboard</h1>
                <p className="text-gray-500">Manage startup funding applications and investments</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
            >
              <Settings className="w-5 h-5" />
              Settings
            </motion.button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Filter className="text-green-600 text-lg" />
            <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Briefcase className="text-xs" />
                Sector
              </label>
              <input
                type="text"
                name="sector"
                value={filters.sector}
                onChange={handleFilterChange}
                placeholder="Search sector..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <MapPin className="text-xs" />
                Location
              </label>
              <input
                type="text"
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                placeholder="Search location..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Award className="text-xs" />
                Stage
              </label>
              <input
                type="text"
                name="stage"
                value={filters.stage}
                onChange={handleFilterChange}
                placeholder="Search stage..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="text-xs" />
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="text-xs" />
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 opacity-0">Clear</label>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearFilters}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-2 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm"
              >
                <RefreshCw className="text-sm" />
                Clear
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Calendar className="text-sm" />
                      Created
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Building2 className="text-sm" />
                      Startup
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Logo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Briefcase className="text-sm" />
                      Sector
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Award className="text-sm" />
                      Stage
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Description</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <MapPin className="text-sm" />
                      Location
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <CreditCard className="text-sm" />
                      Payment
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Heart className="text-sm" />
                      Interests
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="ml-3 text-gray-500">Loading fundings...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredRows.length > 0 ? (
                    filteredRows.map((funding, index) => (
                      <motion.tr
                        key={funding._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-green-50/50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(funding.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {funding.startupDetails?.startupName || "No Name"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200">
                            <img
                              src={funding.logoUrl}
                              alt="Logo"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {funding.sector}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {funding.stage}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">{funding.shortDescription}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="text-xs" />
                            {funding.location}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingStatusId === funding._id ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                              >
                                {["waiting", "on hold", "approved"].map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleStatusChange(funding._id, newStatus)}
                                disabled={!newStatus}
                                className="p-1 text-green-600 hover:bg-green-100 rounded-full transition-colors duration-200"
                              >
                                <Save className="text-sm" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setEditingStatusId(null)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors duration-200"
                              >
                                <X className="text-sm" />
                              </motion.button>
                            </div>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditingStatusId(funding._id)
                                setNewStatus(funding.status || "waiting")
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${STATUS_COLORS[funding.status] || "bg-gray-100 text-gray-800 border-gray-200"
                                }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${funding.status === "approved"
                                  ? "bg-green-500"
                                  : funding.status === "on hold"
                                    ? "bg-yellow-500"
                                    : "bg-blue-500"
                                  }`}
                              ></div>
                              {funding.status || "Pending"}
                            </motion.button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${PAYMENT_STATUS_COLORS[funding.paymentStatus] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
                            <div className={`w-2 h-2 rounded-full mr-2 ${funding.paymentStatus === "pending" ? "bg-yellow-500" : funding.paymentStatus === "completed" ? "bg-green-500" : "bg-gray-500"}`}></div>
                            {funding.paymentStatus || "Unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleViewInterests(funding._id)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium ${getInterestsForFunding(funding._id).length > 0
                              ? "bg-pink-100 text-pink-800 hover:bg-pink-200"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                          >
                            <Heart className="w-3 h-3" />
                            {getInterestsForFunding(funding._id).length} Interests
                          </motion.button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleView(funding)}
                              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                            >
                              <Eye className="text-xs" />
                              View
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setShowCancelPopup(funding._id)}
                              className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:shadow-lg transition-all duration-300 flex items-center gap-1"
                            >
                              <Trash2 className="text-xs" />
                              Cancel
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center py-16">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                          <TrendingUp className="text-gray-400 text-3xl" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No fundings found</h3>
                        <p className="text-gray-500">Try adjusting your filters to see more results</p>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Cancel Confirmation Modal */}
      <AnimatePresence>
        {showCancelPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Cancellation</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to cancel this funding application? This will permanently remove it from the
                system.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelPopup(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleCancel(showCancelPopup)
                    setShowCancelPopup(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Yes, Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Funding Details Modal */}
      <AnimatePresence>
        {selectedFunding && (
          <ModalOverlay onClose={() => setSelectedFunding(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Funding Details</h2>
                  <button
                    onClick={() => setSelectedFunding(null)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Section: Video + Description */}
                  <div className="space-y-6">
                    <div className="w-full h-64 lg:h-400 rounded-xl overflow-hidden border border-gray-200">
                      <iframe
                        src={getEmbedUrl(selectedFunding.youtube)}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Startup Video"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        About the Startup
                      </h3>
                      <p className="text-gray-700 leading-relaxed">{selectedFunding.longDescription}</p>
                    </div>
                  </div>

                  {/* Right Section: Company Info + Stats */}
                  <div className="space-y-6">
                    {/* Company Header */}
                    <div className="text-center space-y-4">
                      <div className="w-24 h-24 mx-auto rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm">
                        <img
                          src={selectedFunding.logoUrl}
                          alt="Logo"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{selectedFunding.companyName}</h2>
                        <div className="flex items-center justify-center gap-4 mt-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {selectedFunding.sector}
                          </span>
                          <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            {selectedFunding.location}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-2">{selectedFunding.shortDescription}</p>
                      </div>
                    </div>

                    {/* Funding Details */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        Funding Information
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Amount Seeking</p>
                          <p className="text-xl font-bold text-green-700">
                            ₹{selectedFunding?.amountSeeking?.toLocaleString?.() || "N/A"}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Equity Offered</p>
                          <p className="text-xl font-bold text-gray-900">{selectedFunding?.equityOffered ?? "N/A"}%</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Valuation</p>
                          <p className="text-xl font-bold text-indigo-700">
                            ₹{selectedFunding?.valuation?.toLocaleString?.() || "N/A"}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Min Investment</p>
                          <p className="text-xl font-bold text-gray-900">
                            ₹{selectedFunding?.minimumInvestment?.toLocaleString?.() || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Fund Usage</p>
                          <p className="text-gray-900">{selectedFunding?.fundUsage ?? "N/A"}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm font-medium text-gray-600">Role for Investors</p>
                          <p className="text-gray-900">{selectedFunding?.roleProvided ?? "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                          Funding Progress
                        </h4>
                        {!isEditingAmount ? (
                          <button
                            onClick={() => setIsEditingAmount(true)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Edit Amount
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={newAmountRaised}
                              onChange={(e) => setNewAmountRaised(Number(e.target.value))}
                              className="border border-gray-300 rounded-lg px-3 py-1 text-sm w-32"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateAmountRaised(selectedFunding._id, newAmountRaised)
                                }
                              }}
                            />
                            <button
                              onClick={() => updateAmountRaised(selectedFunding._id, newAmountRaised)}
                              className="text-green-600 hover:text-green-800 p-1"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingAmount(false)
                                setNewAmountRaised(selectedFunding.amountRaised || 0)
                              }}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-700">Amount Raised:</span>
                          <span className="font-bold text-green-700">₹{newAmountRaised?.toLocaleString() || "0"}</span>
                        </div>
                        <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            animate={{
                              width: `${Math.min(
                                ((newAmountRaised || 0) / (selectedFunding?.amountSeeking || 1)) * 100,
                                100
                              )}%`,
                            }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full"
                          />
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>
                            {Math.min(
                              (((newAmountRaised || 0) / (selectedFunding?.amountSeeking || 1)) * 100).toFixed(2),
                              100,
                            )}
                            % funded
                          </span>
                          <span>Target: ₹{selectedFunding?.amountSeeking?.toLocaleString() || "0"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Interests Modal */}
      {/* Interests Modal */}
      <AnimatePresence>
        {showInterestsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-pink-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Investor Interests</h2>
                  </div>
                  <button
                    onClick={() => setShowInterestsModal(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {currentFundingInterests.length} investor(s) interested in this funding
                </p>
              </div>

              <div className="p-6">
                {currentFundingInterests.length > 0 ? (
                  <div className="space-y-4">
                    {currentFundingInterests.map((interest) => (
                      <motion.div
                        key={interest._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">{interest.name}</h3>
                            <p className="text-sm text-gray-600">{interest.email}</p>
                            <p className="text-sm text-gray-600">{interest.phone}</p>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            {new Date(interest.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Registered: {new Date(interest.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-pink-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No interests yet</h3>
                    <p className="text-gray-500">This funding hasn't received any investor interests yet.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSettingsModal && (
          <ModalOverlay onClose={() => setShowSettingsModal(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Funding Settings
                  </h2>
                  <button
                    onClick={() => setShowSettingsModal(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Funding Registration Amount
                  </h3>

                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        className="border rounded px-2 py-1"
                      />
                      <button onClick={handleSave} className="px-3 py-1 bg-green-600 text-white rounded">
                        Save
                      </button>
                      <button onClick={() => setIsEditing(false)} className="px-3 py-1 bg-gray-300 rounded">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-xl">₹{amount.toLocaleString()}</span>
                      <button onClick={() => setIsEditing(true)} className="px-3 py-1 bg-blue-500 text-white rounded">
                        Edit
                      </button>
                    </div>
                  )}

                  <p className="text-sm text-gray-500 mt-3">
                    This is the amount startups need to pay to register for funding on your platform.
                  </p>
                </div>
              </div>
            </div>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </div >
  )
}

export default FundingDashboard